import { Component, OnInit } from '@angular/core';
import { FormBuilder, MaxLengthValidator, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { countriesOptions } from 'src/app/data/countries';
import { statesOptions } from 'src/app/data/states';
import Utils, { GetHelper, UtilsValidators } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

export type PersonIncomeRealtyValues = {
  type: string;
  current_value: string;
  address: {
    cep: string;
    public_place: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    uf: string;
    country: string;
  };
};
@Component({
  selector: 'app-signup-unified-person-income-realty',
  templateUrl: './signup-unified-person-income-realty.component.html',
  styleUrls: ['./signup-unified-person-income-realty.component.scss'],
})
export class SignupUnifiedPersonIncomeRealtyComponent implements OnInit {
  form = this.formBuilder.group({
    type: [null],
    current_value: [null],

    cep: [null, [UtilsValidators.cep]],
    public_place: [null, []],
    number: [null, []],
    complement: [null, []],
    district: [null, []],
    city: [null, []],
    uf: [null, []],
    country: [null, []],
  });

  columns: TableColumn<any>[] = [
    { name: 'Tipo do imóvel', cell: (row) => row.type },
    {
      name: 'Endereço completo',
      cell: (row) => `
      ${
        row.address && row.address.cep
          ? ` <span>
            ${row.address.public_place}, ${row.address.number}, ${
              row.address.complement ?? ''
            } ${row.address.district}
            </span>
            <span class="table-subtitle">${row.address.city}, ${
              row.address.uf
            } — ${row.address.cep}</span>`
          : ''
      }

      `,
    },
    {
      name: 'Valor atual',
      cell: (row) => Utils.formatBrl(Number(row.current_value)),
    },
  ];

  data: any[] = [];

  footerData: any[] = [];

  showForm = true;

  zipcodeFetched = false;
  loadingZipcode = false;

  addingRealty = false;
  removingRealty = false;
  loading = false;
  showDeleteModal = false;
  selectedIndexToDelete: number | null = null;

  states = statesOptions;
  countries = countriesOptions;

  constructor(
    private formBuilder: FormBuilder,
    private utilsService: UtilsService,
    private signupService: SignupService,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Para cotista PF, bens imóveis são opcionais, sempre retorna true
    this.signupService.setPersonFormStatus('incomeRealty', true);
    
    this.getData();

    this.form.controls.cep.valueChanges.subscribe((value) => {
      if (value.length > 3) {
        this.form.controls.public_place.setValidators([Validators.required]);
        this.form.controls.number.setValidators([Validators.required]);
        this.form.controls.district.setValidators([Validators.required]);
        this.form.controls.city.setValidators([Validators.required]);
        this.form.controls.uf.setValidators([Validators.required]);
      } else {
        this.form.controls.public_place.setValidators([]);
        this.form.controls.number.setValidators([]);
        this.form.controls.district.setValidators([]);
        this.form.controls.city.setValidators([]);
        this.form.controls.uf.setValidators([]);
        this.zipcodeFetched = false;

        this.form.controls.public_place.setValue(null);
        this.form.controls.number.setValue(null);
        this.form.controls.district.setValue(null);
        this.form.controls.city.setValue(null);
        this.form.controls.uf.setValue(null);
      }

      this.form.controls.public_place.updateValueAndValidity();
      this.form.controls.number.updateValueAndValidity();
      this.form.controls.district.updateValueAndValidity();
      this.form.controls.city.updateValueAndValidity();
      this.form.controls.uf.updateValueAndValidity();
    });
  }

  setFooterData() {
    const total = String(
      this.data.reduce(
        (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
        0
      )
    );

    this.footerData = [
      {
        type: '<span><strong>Total declarado<strong></span>',

        current_value: total,
      },
    ];
  }

  async getData() {
    this.loading = true;
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');
      
      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.loading = false;
        return;
      }
      
      const { data } = await this.api.get({
        route: 'api/registration/properties/',
        params: { register_uuid: routeId },
        token: true,
      });

      this.data = data;

      this.signupService.setPersonFormStatus('incomeRealty', true);

      this.showForm = this.data.length === 0;

      this.setFooterData();
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  async addNewRealty() {
    this.addingRealty = true;
    try {
      const valuesHelper = new GetHelper(this.form.value);

      let payload: any = {
        type: valuesHelper.get('type'),
        current_value: valuesHelper.get('current_value'),
      };

      if (valuesHelper.get('cep')) {
        payload = {
          ...payload,
          address: [
            {
              cep: valuesHelper.get('cep'),
              public_place: valuesHelper.get('public_place'),
              number: valuesHelper.get('number'),
              complement: valuesHelper.get('complement'),
              district: valuesHelper.get('district'),
              city: valuesHelper.get('city'),
              uf: valuesHelper.get('uf'),
              country: valuesHelper.get('country'),
              type: 1,
            },
          ],
        };
      }

      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');
      
      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.addingRealty = false;
        return;
      }
      
      await this.api.post({
        route: 'api/registration/properties/',
        params: { register_uuid: routeId },
        token: true,
        body: payload,
      });

      this.zipcodeFetched = false;
      this.form.reset();

      // Ativar loading antes de recarregar os dados
      await this.getData();
    } catch (error) {
      console.log(error);
    } finally {
      this.addingRealty = false;
    }
  }

  handleOpenDeleteModal(index: number) {
    this.selectedIndexToDelete = index;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.selectedIndexToDelete = null;
  }

  async confirmDeleteRealty() {
    if (this.selectedIndexToDelete === null) {
      return;
    }

    this.removingRealty = true;
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');
      
      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.removingRealty = false;
        return;
      }
      
      await this.api.delete({
        route: `api/registration/properties/${this.data[this.selectedIndexToDelete].id}/`,
        params: { register_uuid: routeId },
        token: true,
      });

      // Ativar loading antes de recarregar os dados
      await this.getData();
    } catch (error) {
      console.log(error);
    } finally {
      this.removingRealty = false;
      this.closeDeleteModal();
    }
  }

  getDisabled() {
    if (this.showForm) {
      return this.form.invalid;
    }

    return false;
  }

  triggerForm() {
    if (this.showForm) {
      this.addNewRealty();
    }

    this.showForm = !this.showForm;
  }

  handleCancel() {
    this.showForm = false;
    this.form.reset();
  }

  async searchZipcode(event: any) {
    const zipcode = event.target.value;

    // Só busca se tiver 8 dígitos (CEP completo)
    const zipcodeNumbers = zipcode?.replace(/\D/g, '') || '';
    if (zipcodeNumbers.length !== 8) {
      this.loadingZipcode = false;
      this.zipcodeFetched = false;
      return;
    }

    this.loadingZipcode = true;
    try {
      const zipcodeRes = await this.utilsService.getZipcode(zipcode);

      if (zipcodeRes) {
        this.form.patchValue({
          public_place: zipcodeRes.logradouro,
          district: zipcodeRes.bairro,
          city: zipcodeRes.localidade,
          uf: zipcodeRes.uf,
          country: '1',
        });

        this.zipcodeFetched = true;
      } else {
        this.zipcodeFetched = false;
      }
    } catch (error) {
      this.zipcodeFetched = false;
      console.error(error);
    } finally {
      this.loadingZipcode = false;
    }
  }
}

